import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { getFileExtension } from "../../utils/file/filePath";
import { listFiltersService } from "../../utils/list-filters/ListFiltersService";
import { ListTableSort } from "../lists/ListTableSort";
import { BucketObjectItem } from "./BucketObjectItem";
import { BucketObjectType } from "./types/BucketObjectType";

export interface BucketExplorerFilters {
    path: string | undefined;
    fileName: string;
}

const defaultFilters: BucketExplorerFilters = {
    path: undefined,
    fileName: ""
};

export const useBucketExplorerFilters = () => {
    const location = useLocation();
    const history = useHistory();

    const filtersService = useMemo(() => listFiltersService<BucketExplorerFilters>(defaultFilters), []);

    const [pathFolders, setPathFolders] = useState<string[]>(() => {
        const { path } = filtersService.getSavedFilters(location.search);

        return path?.split("\\") ?? [];
    });

    const [sort, setSort] = useState<ListTableSort>({ by: "type", order: "asc" });
    const [fileName, setFileName] = useState<string | undefined>(() => {
        const { fileName: name } = filtersService.getSavedFilters(location.search);

        return name;
    });

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, {
            path: pathFolders.length ? pathFolders.join("\\") : undefined,
            fileName: fileName ?? ""
        });
    }, [location.pathname, history, pathFolders, fileName]);

    const sortBucketContent = (content: BucketObjectItem[], sortConfig: ListTableSort): BucketObjectItem[] => {
        const contentCopy = [...content];
        switch (sortConfig.by) {
            case "name": {
                sortByName(contentCopy, sortConfig.order === "desc");
                break;
            }

            case "lastModified": {
                sortByLastModified(contentCopy, sortConfig.order === "desc");
                break;
            }

            case "type": {
                sortByType(contentCopy, sortConfig.order === "desc");
                break;
            }

            default: {
                throw new Error(`Sort by '${sortConfig.by}' is not supported`);
            }
        }

        return contentCopy;
    };

    const openFolder = (folderName: string) => {
        setPathFolders((items) => [...items, folderName]);
    };

    const goBackToFolder = (folderName: string) => {
        setPathFolders((items) => items.slice(0, items.findIndex((folder) => folder === folderName) + 1));
    };

    const root = (): void => {
        setPathFolders([]);
    };

    return {
        fileName,
        pathFolders,
        sort,
        openFolder,
        goBackToFolder,
        root,
        setFileName,
        setSort,
        sortBucketContent
    };
};

const sortByName = (content: BucketObjectItem[], reverse?: boolean): void => {
    content.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

    if (reverse) {
        content.reverse();
    }
};

const sortByLastModified = (content: BucketObjectItem[], reverse?: boolean): void => {
    content.sort((a, b) => {
        if (a.type === BucketObjectType.Folder || b.type === BucketObjectType.Folder) {
            return b.type === BucketObjectType.Folder ? 1 : -1;
        }

        return new Date(a.lastModified) > new Date(b.lastModified) ? 1 : -1;
    });

    content.sort((a, b) => {
        if (a.lastModified === b.lastModified) {
            return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        }

        return 0;
    });

    if (reverse) {
        content.reverse();
    }
};

const sortByType = (content: BucketObjectItem[], reverse: boolean): void => {
    content.sort((a, b) => {
        if (a.type === BucketObjectType.Folder || b.type === BucketObjectType.Folder) {
            return b.type === BucketObjectType.Folder ? 1 : -1;
        }

        return getFileExtension(a.name) > getFileExtension(b.name) ? 1 : -1;
    });

    content.sort((a, b) => {
        if (a.type === b.type && getFileExtension(a.name) === getFileExtension(b.name)) {
            return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
        }

        return 0;
    });

    if (reverse) {
        content.reverse();
    }
};
